import { Skeleton } from "@/components/shared/Skeleton";
import { RosterRowSkeleton } from "@/components/skeletons";
import { AppButton } from "@/components/teacher/AppButton";
import { StatPill } from "@/components/teacher/StatPill";
import { format, useT } from "@/i18n";
import { CLASSROOM_ROOT, type ClassroomScope } from "@/pages/teacher/classroomScope";
import { useClassroomRoster } from "@/pages/teacher/useClassroomData";
import { Link } from "@tanstack/react-router";
import { HiChevronRight } from "react-icons/hi";

export function ClassroomStudents({
  classroomId,
  scope,
}: { classroomId: string; scope: ClassroomScope }) {
  const t = useT();

  const root = CLASSROOM_ROOT[scope];
  const { students, loading, error, refetch } = useClassroomRoster(scope, classroomId);

  if (error) {
    return (
      <div className="app-card space-y-3 p-6 text-center">
        <p className="body text-app-muted">{t["roster.loadError"]}</p>
        <AppButton tone="quiet" onClick={refetch}>
          {t["common.tryAgain"]}
        </AppButton>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="app-card space-y-3 p-4 sm:p-5">
        <Skeleton className="h-7 w-40" />
        <ul className="divide-y divide-gray-100">
          {["a", "b", "c", "d", "e", "f"].map((key) => (
            <RosterRowSkeleton key={key} />
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="app-card space-y-3 p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3">
        <span className="app-title">{t["roster.title"]}</span>
        <StatPill>{format(t["roster.enrolled"], { count: students.length })}</StatPill>
      </div>

      <ul className="divide-y divide-gray-100">
        {students.map((student) => {
          const details = (
            <span className="min-w-0 flex-1">
              <span className="block truncate font-kumbh text-base font-extrabold text-app-ink">
                {student.name}
              </span>
              <span className="caption block truncate text-app-muted">{student.username}</span>
            </span>
          );

          // Without a local account there is nothing to show: homework is keyed by user id.
          return (
            <li key={student.myBestUserId}>
              {student.userId ? (
                <Link
                  to={`${root}/$classroomId/students/$studentId`}
                  params={{ classroomId, studentId: student.userId }}
                  className="-mx-2 flex items-center gap-3 rounded-2xl px-2 py-3 hover:bg-app-sky-soft"
                >
                  {details}
                  <HiChevronRight className="h-4 w-4 shrink-0 text-app-ink" />
                </Link>
              ) : (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3">
                  {details}
                  <StatPill tone="muted">{t["roster.neverSignedIn"]}</StatPill>
                </div>
              )}
            </li>
          );
        })}

        {students.length === 0 && (
          <li className="body-sm py-3 text-app-muted">{t["roster.empty"]}</li>
        )}
      </ul>
    </div>
  );
}
